import {
	DesignSystemVersion,
	DsFile,
	DsFileChange,
	ChatRef,
	DsVersionRef,
} from 'dragonmouse-shared/interfaces';
import { VITE_FORGE_URL } from '@constants';
import {
	clearActiveDesignSystem,
	updateDsChatRefs,
	updateDsVersionRefs,
	updateDraftChanges,
	setDsPreparing,
	setDsMigrating,
	setDsHasDraft,
	setDsTitle,
	setDsCanWrite,
	setDesignSystemLoadError,
	setLoadingState,
	updateActiveDesignSystemVersion,
} from '../redux/db/actions';
import { setDesignSystemContent, resolveOptimisticDsFileWrites } from '../redux/mode/actions';
import { createDesignSystemContentFromFiles } from '../designSystem/content/factory';
import { applyDsFileWrites, findResolvedDsFileWrites } from '../redux/mode/util/dsFiles';
import store from '../redux/store';
import { PollingStream } from './PollingStream';

export class DesignSystemVersionPollingStream extends PollingStream<DesignSystemVersion | null> {
	private lastFiles: DsFile[] | null = null;
	private lastChatRefs: ChatRef[] = [];
	private lastVersionRefs: DsVersionRef[] = [];
	private lastPreparing: boolean = false;
	private lastHasDraft: boolean = false;
	private lastTitle: string = '';
	private lastCanWrite: boolean = false;
	private lastDraftChanges: DsFileChange[] | undefined = undefined;
	private isHistoricalView: boolean;

	constructor(designSystemId: string, versionNumber?: number) {
		const base = VITE_FORGE_URL + `/poll/design-system-version/${designSystemId}`;
		const url = versionNumber != null ? `${base}?version=${versionNumber}` : base;

		const onError = (error: Error) => {
			if (error.message === 'Access denied') {
				store.dispatch(setDesignSystemLoadError('You do not have access to this design system'));
				store.dispatch(clearActiveDesignSystem());
				store.dispatch(setDesignSystemContent(null));
			} else {
				console.error('DesignSystemVersionPollingStream error:', error);
			}
		};

		super(url, onError, 'activeDesignSystemVersion');
		this.isHistoricalView = versionNumber != null;
	}

	extractData(response: any): DesignSystemVersion[] {
		// DesignSystemVersion endpoint returns a single DesignSystemVersion object or null
		if (response?.version === null || response?.version === undefined) {
			this.lastFiles = null;
			this.lastChatRefs = [];
			this.lastVersionRefs = [];
			this.lastPreparing = false;
			this.lastHasDraft = false;
			this.lastTitle = '';
			this.lastCanWrite = false;
			this.lastDraftChanges = undefined;
			return [];
		}

		this.lastFiles = response.files || null;
		this.lastChatRefs = response.chatRefs || [];
		this.lastVersionRefs = response.versionRefs || [];
		this.lastPreparing = response.preparing || false;
		this.lastHasDraft = response.hasDraft || false;
		this.lastTitle = response.title || '';
		this.lastCanWrite = response.canWrite || false;
		this.lastDraftChanges = response.draftChanges || undefined;

		return [response.version];
	}

	onData(versions: DesignSystemVersion[]) {
		const version = versions.length > 0 ? versions[0] : null;
		if (!version) {
			store.dispatch(clearActiveDesignSystem());
			store.dispatch(setDesignSystemContent(null));
			return;
		}

		// The backend already returns DesignSystemVersion objects with proper camelCase field names
		// (thanks to postgres.camel transform)
		// We just need to convert date strings to Date objects
		const processedVersion: DesignSystemVersion = {
			...version,
			createdAt: new Date(version.createdAt),
			updatedAt: new Date(version.updatedAt),
		};

		// since DS versions don't have optimistic writes (they aren't changed on the FE currently) we don't need to resolve them
		store.dispatch(updateActiveDesignSystemVersion(processedVersion));

		// Process chat refs - convert date strings to Date objects
		const processedChatRefs: ChatRef[] = this.lastChatRefs.map((ref) => ({
			...ref,
			updatedAt: new Date(ref.updatedAt),
		}));
		// Marked here rather than by the stream's own loading resource: a poll can
		// finish without a version, and returns above before reaching this, so only
		// this line means the chat list is actually present.
		store.dispatch(updateDsChatRefs(processedChatRefs));
		store.dispatch(setLoadingState({ resource: 'dsChatRefs', state: 'loaded' }));

		// Update version refs - convert date strings to Date objects
		const processedVersionRefs: DsVersionRef[] = this.lastVersionRefs.map((ref) => ({
			...ref,
			createdAt: new Date(ref.createdAt),
		}));
		store.dispatch(updateDsVersionRefs(processedVersionRefs));
		store.dispatch(setDsPreparing(this.lastPreparing));
		const needsMigration =
			!this.isHistoricalView &&
			(this.lastFiles?.length ?? 0) > 0 &&
			processedVersion.manifest?.promptsMigrated !== true;
		store.dispatch(setDsMigrating(needsMigration));
		store.dispatch(setDsHasDraft(this.lastHasDraft));
		store.dispatch(setDsTitle(this.lastTitle));
		store.dispatch(setDsCanWrite(this.lastCanWrite));
		store.dispatch(updateDraftChanges(this.lastDraftChanges || null));

		// Process files, applying optimistic writes and resolving outcome-matched writes.
		const serverFiles = this.lastFiles || [];
		const state = store.getState();
		const writes = state.mode.optimisticDsFileWrites;

		if (serverFiles.length > 0 || writes.length > 0) {
			const resolvedIds = findResolvedDsFileWrites(writes, serverFiles);
			if (resolvedIds.length > 0) {
				store.dispatch(resolveOptimisticDsFileWrites(resolvedIds));
			}

			const remainingWrites = writes.filter((w) => !resolvedIds.includes(w.id));
			const patchedFiles = applyDsFileWrites(serverFiles, remainingWrites);

			const content = createDesignSystemContentFromFiles(processedVersion.id, patchedFiles);
			store.dispatch(setDesignSystemContent(content));
		} else {
			// If no files, set content to null and clear optimistic writes
			store.dispatch(setDesignSystemContent(null));
		}
	}
}
